import { type MaybeRefOrGetter } from 'vue'
import { PrefixTranslationsEnum } from '@/common/types/i18n'
import { type CaseActionParamsDTOV1 } from '@/common/types/case'
import { type FieldContextEnum } from '@/common/types/fields'
import type { ListScreensParamsV1, ListScreensParamsV3ByCode } from '@/common/types/screen'
import { type UserParamsDTOV3 } from '@/common/types/users'
import { type SkillParamsDTOV2 } from '@/common/types/skills'
import { type ReasonsParamsDTOV1 } from '@/common/types/reasons'

export const SERVER_DATA_TYPE = {
  LIST: 'LIST',
  ITEM: 'ITEM',
} as const

export const SERVER_DATA_SCOPE = {
  CASE_ACTIONS: 'CASE_ACTIONS',
  CHANNELS: 'CHANNELS',
  CUSTOM_FIELDS: 'CUSTOM_FIELDS',
  EMERGENCIES: 'EMERGENCIES',
  FIELDS: 'FIELDS',
  FILE_DEFINITION: 'FILE_DEFINITION',
  I18n: 'I18n',
  INTERACTION_CUSTOM: 'INTERACTION_CUSTOM',
  INTERACTION_TYPES: 'INTERACTION_TYPES',
  LOCALES: 'LOCALES',
  MAILBOXES: 'MAILBOXES',
  PRIORITIES: 'PRIORITIES',
  REASONS: 'REASONS',
  /** @deprecated */
  SCREENS_V1: 'SCREENS_V1',
  SCREENS_V3: 'SCREENS_V3',
  SKILLS: 'SKILLS',
  SPACES: 'SPACES',
  TEAMS: 'TEAMS',
  TYPOLOGIES: 'TYPOLOGIES',
  USERS: 'USERS',
  PRODUCTS: 'PRODUCTS',
} as const

export const SERVER_DATA_LABELS = {
  CASE_ACTIONS: [PrefixTranslationsEnum.TICKETACTIONTYPE],
  CHANNELS: [PrefixTranslationsEnum.INTERACTION_CHANNEL],
  CUSTOM_FIELDS: [PrefixTranslationsEnum.CUSTOMFIELD, PrefixTranslationsEnum.CFOPTION],
  EMERGENCIES: [PrefixTranslationsEnum.EMERGENCY],
  FIELDS: [PrefixTranslationsEnum.ENTITY_FIELD, PrefixTranslationsEnum.CUSTOMFIELD, PrefixTranslationsEnum.CFOPTION],
  INTERACTION_CUSTOM: [PrefixTranslationsEnum.INTERACTION],
  INTERACTION_TYPES: [PrefixTranslationsEnum.INTERACTION_TYPE, PrefixTranslationsEnum.INTERACTION_CHANNEL],
  MAILBOXES: [PrefixTranslationsEnum.SPACE],
  PRIORITIES: [PrefixTranslationsEnum.PRIORITY],
  REASONS: [PrefixTranslationsEnum.REASON],
  /** @deprecated */
  SCREENS_V1: [PrefixTranslationsEnum.SCREEN],
  SCREENS_V3: [PrefixTranslationsEnum.SCREEN],
  SKILLS: [PrefixTranslationsEnum.SPACE, PrefixTranslationsEnum.SKILL],
  SPACES: [PrefixTranslationsEnum.SPACE],
  TEAMS: [PrefixTranslationsEnum.USERGROUP],
  TYPOLOGIES: [PrefixTranslationsEnum.SPACE, PrefixTranslationsEnum.TYPOLOGY],
  PRODUCTS: [PrefixTranslationsEnum.SPACE, PrefixTranslationsEnum.PRODUCT],
}
export type ServerDataScope = (typeof SERVER_DATA_SCOPE)[keyof typeof SERVER_DATA_SCOPE]
export type ServerDataScopeLabels = keyof typeof SERVER_DATA_LABELS

export type ServerDataScopeParams = {
  CASE_ACTIONS: { LIST: CaseActionParamsDTOV1 }
  CUSTOM_FIELDS: { ITEM: MaybeRefOrGetter<string> }
  FIELDS: { LIST: FieldContextEnum }
  I18n: { ITEM: PrefixTranslationsEnum }
  /** @deprecated */
  SCREENS_V1: { LIST: ListScreensParamsV1 }
  SCREENS_V3: { ITEM: ListScreensParamsV3ByCode }
  SKILLS: { LIST: SkillParamsDTOV2 }
  REASONS: { LIST: ReasonsParamsDTOV1 }
  USERS: { LIST: UserParamsDTOV3 }
  SPACES: { ITEM: UUID }
}

export interface GroupOptionsBase {
  //  The time in milliseconds after which data is considered stale. Set 0 to refetch.
  staleTime?: number
}

export interface ServerDataComposableOptionsBase<TResult, TParam> {
  select?: (data: TParam) => TResult
  enabled?: MaybeRefOrGetter<boolean>
  staleTime?: number
}
